import { ChevronRight, ChevronDown, Folder, FolderOpen } from 'lucide-react';
import { useRef, useEffect, useState } from 'react';

import {
  Box,
  Typography,
  IconButton,
  useTheme,
  alpha,
  ListItemButton,
  CircularProgress,
} from '@mui/material';
import { Category } from '../../../api/protosCompiled/buildFoundry/buildFoundry_pb';

interface CategoryNodeProps {
  node: Category.AsObject;
  level: number;
  expandedNodes: Set<string>;
  onToggleExpand: (nodeId: string) => void;
  onSelectCategory: (categoryId: string) => void;
  selectedCategoryId?: string | null;
  onContextMenu: (e: React.MouseEvent, node: Category.AsObject) => void;
  dragState: {
    draggedNode: Category.AsObject | null;
    setDraggedNode: (node: Category.AsObject | null) => void;
    dropTarget: string | 'root' | null;
    setDropTarget: (target: string | 'root' | null) => void;
  };
  isLoading?: boolean;
  operationsInProgress: Set<string>;
  onMoveCategory?: (draggedNode: Category.AsObject, newParentId: string) => void;
}

export const CategoryNode: React.FC<CategoryNodeProps> = ({
  node,
  level,
  expandedNodes,
  onToggleExpand,
  onSelectCategory,
  selectedCategoryId,
  onContextMenu,
  dragState,
  isLoading = false,
  operationsInProgress,
  onMoveCategory,
}) => {
  const theme = useTheme();
  const textRef = useRef<HTMLDivElement>(null);
  const [isTextTruncated, setIsTextTruncated] = useState(false);

  const isExpanded = expandedNodes.has(node.id);
  const isSelected = selectedCategoryId === node.id;
  const isDropTarget = dragState.dropTarget === node.id;
  const hasChildren = node.childrenList && node.childrenList.length > 0;

  // Check if text is truncated
  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        const element = textRef.current;
        const isTruncated = element.scrollWidth > element.clientWidth;
        setIsTextTruncated(isTruncated);
      }
    };

    checkTruncation();
    // Re-check on window resize
    window.addEventListener('resize', checkTruncation);
    return () => window.removeEventListener('resize', checkTruncation);
  }, [node.name]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    console.log('📂 Chevron clicked for node:', node.id, node.name);
    if (hasChildren) {
      onToggleExpand(node.id);
    }
  };

  const handleSelect = (e: React.MouseEvent) => {
    e.stopPropagation();
    console.log('📁 Folder row clicked for node:', node.id, node.name);
    onSelectCategory(node.id);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        ...(isDropTarget && {
          bgcolor: alpha(theme.palette.primary.main, 0.1),
          border: `2px solid ${theme.palette.primary.main}`,
          borderRadius: 1,
        }),
      }}
    >
      <ListItemButton
        sx={{
          pl: level * 2 + 0.5,
          py: 0.5,
          minHeight: 36,
          '&:hover': {
            bgcolor: alpha(theme.palette.action.hover, 0.1),
          },
          ...(isSelected && {
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.main,
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.15),
            },
          }),
        }}
        onClick={handleSelect}
        onContextMenu={e => onContextMenu(e, node)}
      >
        {/* Expand/Collapse Icon */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 16,
            height: 16,
            mr: 0.5,
          }}
        >
          {hasChildren ? (
            <IconButton
              size="small"
              onClick={handleToggle}
              sx={{
                p: 0,
                color: theme.palette.text.secondary,
                '&:hover': {
                  color: theme.palette.text.primary,
                  bgcolor: alpha(theme.palette.action.hover, 0.1),
                },
              }}
              title={isExpanded ? 'Collapse folder' : 'Expand folder'}
            >
              {isExpanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            </IconButton>
          ) : (
            <Box sx={{ width: 12, height: 12 }} />
          )}
        </Box>

        {/* Folder Icon */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 16,
            height: 16,
            mr: 0.5,
          }}
        >
          {isLoading ? (
            <CircularProgress size={16} sx={{ color: theme.palette.primary.main }} />
          ) : isExpanded ? (
            <FolderOpen size={16} style={{ color: theme.palette.primary.main }} />
          ) : (
            <Folder size={16} style={{ color: theme.palette.text.secondary }} />
          )}
        </Box>

        {/* Category Name */}
        <Typography
          ref={textRef}
          variant="body2"
          sx={{
            flex: 1,
            fontWeight: 500,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            minWidth: 0, // Allow flex item to shrink below content size
            ...(isSelected && {
              fontWeight: 600,
            }),
          }}
        >
          {node.name}
        </Typography>

        {/* Item Count - Only show if text is not truncated and there are items */}
        {node.totalItemCount !== undefined && node.totalItemCount > 0 && !isTextTruncated && (
          <Typography
            variant="caption"
            sx={{
              ml: 0.5,
              color: theme.palette.text.secondary,
              fontSize: '0.75rem',
              fontWeight: 500,
              flexShrink: 0, // Prevent count from shrinking
            }}
          >
            ({node.totalItemCount})
          </Typography>
        )}
      </ListItemButton>

      {/* Children */}
      {isExpanded && hasChildren && (
        <Box>
          {node.childrenList!.map(child => (
            <CategoryNode
              key={child.id}
              node={child}
              level={level + 1}
              expandedNodes={expandedNodes}
              onToggleExpand={onToggleExpand}
              onSelectCategory={onSelectCategory}
              selectedCategoryId={selectedCategoryId}
              onContextMenu={onContextMenu}
              dragState={dragState}
              isLoading={operationsInProgress.has(child.id)}
              operationsInProgress={operationsInProgress}
              onMoveCategory={onMoveCategory}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};
